import { InputNumber, Button, Panel, ButtonGroup, Badge } from "rsuite";
import Accordion from 'rsuite/Accordion';
import { useState } from "react";
import "./_app-info.scss";
import { PanelService } from "network/panel/network";
import { IApp } from "@bprotsyk/aso-core/lib/app/app";
import ReactCountryFlag from 'react-country-flag';

interface Offer {
    id: string;
    name: string;
    graphicFolder: string;
    geo: string;
    caption: string;
    partner: string;
}

interface Section {
    offers: Offer[];
    platformGeos: string[];
    title: string;
}

interface Platforms {
    [key: string]: {
        sections: Section[];
        platformGeos: string[];
        enabled: boolean;
    };
}

interface OfferInfo {
    geosRoot: string;
    appid: number;
    sections: Section[];
    platforms?: Platforms;
}

export default function AppInfo() {
  const [appId, setAppId] = useState<number | null>(null);
  const [offers, setOffers] = useState<OfferInfo | null>(null);
  const [selectedGeos, setSelectedGeos] = useState<Record<string, string | null>>({});
  const [expandedPlatforms, setExpandedPlatforms] = useState<Record<string, boolean>>({});
  const [expandedOffers, setExpandedOffers] = useState<Record<string, boolean>>({});

  const handleSubmit = () => {
    if (appId) {
      PanelService.getAppInfo(appId).then((res) => {
        setOffers(res?.data);
        setSelectedGeos({});
        setExpandedPlatforms({});
        setExpandedOffers({});
      });
    }
  };

  const renderOffers = (offers: Offer[], platform: string) => {
    const selectedGeo = selectedGeos[platform];
    const filteredOffers = offers.filter(offer => !selectedGeo || offer.geo === selectedGeo);
    
    return filteredOffers.map((offer) => (
     <>
     {offer && 
      <div key={offer.id} className="offer-container">
      <Panel 
        header={offer.name}
        collapsible 
        bordered
        className="offer-panel"
        style={{
          backgroundImage: `url(https://cg-data-server.com/static/offers/images/${offer.graphicFolder}/bg.png)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <p className="offer-text">ID: {offer.id}</p>
        <p className="offer-text">Name: {offer.name}</p>
        <p className="offer-text">Geo: {offer.geo}</p>
        <p className="offer-text">Partner: {offer.partner}</p>
      </Panel>
      <div className="offer-logo-wrapper">
        <img 
          src={`https://cg-data-server.com/static/offers/images/${offer.graphicFolder}/logo.png`} 
          alt=""
          className="offer-logo_app_info"
        />
      </div>
    </div>
     }
     </>
    ));
  };

  const renderGeoButtons = (geos: string[], platform: string) => (
    <ButtonGroup className="geo-buttons">
      {geos.map(geo => (
        <Button
          key={geo}
          active={selectedGeos[platform] === geo}
          onClick={() => setSelectedGeos(prev => ({
            ...prev,
            [platform]: prev[platform] === geo ? null : geo
          }))}
        >
          <ReactCountryFlag countryCode={geo} svg style={{ marginRight: '8px' }} />
          {geo}
        </Button>
      ))}
    </ButtonGroup>
  );

  const renderSections = (sections: Section[], platform: string) => (
    sections.map((section, index) => (
      <div key={index} className="section-container">
        {section.offers.length > 0 && (
          <div className="section-offers">
            {section.title && <h4>{section.title}</h4>}
            {renderOffers(section.offers, platform)}
          </div>
        )}
      </div>
    ))
  );

  const sortPlatforms = (platforms: Platforms) => {
    return Object.entries(platforms).sort(([a], [b]) => {
      if (a === '@') return -1;
      if (b === '@') return 1;
      return a.localeCompare(b);
    });
  };

  const handlePlatformExpand = (platform: string, expanded: boolean) => {
    setExpandedPlatforms(prev => ({
      ...prev,
      [platform]: expanded
    }));
  };

  return (
    <div className="app-info">
      <div className="app-info-header">
        <div className="app-info-header-content">
          {offers && (
            <div className="app-header-title">
              #{offers.appid}
            </div>
          )}
          <div className="app-info-actions">
            <InputNumber 
              className="app-id-input"
              size="lg"
              placeholder="Введіть App ID"
              min={0}
              value={appId}
              onChange={value => setAppId(value as number)}
            />
            <Button 
              appearance="primary" 
              size="lg"
              onClick={handleSubmit}
              disabled={!appId}
            >
              Пошук
            </Button>
          </div>
        </div>
      </div>

      <div className="content-wrapper">
        {offers && (
          <div className="app-info-container">
            <div className="app-header-panel">
              {offers.platforms && (
                <div className="platforms-container">
                  {sortPlatforms(offers.platforms).map(([platform, data]) => (
                    <div key={platform} className="platform-section">
                      <div className="platform-header" onClick={() => handlePlatformExpand(platform, !expandedPlatforms[platform])}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span>Платформа: {platform.toUpperCase()}</span>
                          <Badge color={data.enabled ? "green" : "red"} content={data.enabled ? "Увімкнено" : "Вимкнено"} />
                        </div>
                        <div className="platform-geos">
                          {data.platformGeos.map(geo => (
                            <ReactCountryFlag 
                              key={geo} 
                              countryCode={geo} 
                              svg 
                              style={{ marginLeft: '4px' }} 
                            />
                          ))}
                        </div>
                      </div>
                      
                      {expandedPlatforms[platform] && (
                        <div className="platform-content">
                          {renderGeoButtons(data.platformGeos, platform)}
                          {renderSections(data.sections, platform)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


